import { useEffect } from "react";
import { create } from "zustand";
import { api } from "../api/client";
import { observeSessionStream, type StreamHandle } from "../api/stream";
import { ApiError } from "../api/types";
import type {
  AgentEvent,
  ChatMessage,
  ChatSession,
  SessionDetailResponse,
} from "../api/types";

export type AgentStatus =
  | "idle"
  | "streaming"
  | "awaiting_clarify"
  | "error"
  | "cancelled";

export interface StepItem {
  index: number;
  tool: string;
  toolInput?: Record<string, unknown>;
  status: "running" | "done" | "error";
  durationMs?: number;
}

/**
 * RunView is the client-side projection of one session's agent run: a pure
 * fold over the server's event log, plus one optimistic flag (cancelRequested).
 * Keyed by session id in the store, so state can never leak between chats and
 * component mount/unmount needs no "reset" bookkeeping.
 */
export interface RunView {
  status: AgentStatus;
  steps: StepItem[];
  clarifyQuestion: string | null;
  errorCode: string | null;
  /**
   * Replay cursor: the seq of the last event-log entry folded into this view.
   * A re-attach while the view is live resumes right after it instead of
   * replaying the whole run.
   */
  lastSeq: number;
  /**
   * The user asked to cancel this turn. Progress events are ignored from that
   * point (the composer is already unlocked), but the server's terminal
   * verdict — cancelled, or done if the run won the race — still applies.
   */
  cancelRequested: boolean;
}

export const IDLE_RUN: RunView = {
  status: "idle",
  steps: [],
  clarifyQuestion: null,
  errorCode: null,
  lastSeq: 0,
  cancelRequested: false,
};

function freshRun(status: AgentStatus): RunView {
  return { ...IDLE_RUN, status, steps: [] };
}

interface ChatStateData {
  // ─── Session list ──────────────────────────────────────────
  sessions: ChatSession[];
  loadingSessions: boolean;

  // ─── Active session ───────────────────────────────────────
  activeSessionId: number | null;
  messages: ChatMessage[];
  loadingDetail: boolean;

  // ─── Agent runs, keyed by session ─────────────────────────
  sessionRuns: Record<number, RunView>;
}

interface ChatActions {
  // Session list
  loadSessions: () => Promise<void>;
  selectSession: (sessionId: number) => Promise<void>;
  createSession: () => Promise<ChatSession>;
  renameSession: (sessionId: number, name: string) => Promise<void>;
  deleteSession: (sessionId: number) => Promise<void>;

  /**
   * Declare that some component is displaying this session and therefore its
   * run must be observed. Opens the idempotent GET event stream (204 = no
   * run) and keeps the session's RunView folded up to date; ref-counted, so
   * several components can observe the same session. Returns a detach
   * function for the component's unmount — detaching only closes this
   * client's subscription, the server-side run is unaffected.
   *
   * Prefer {@link useSessionRun}, which wires this to the component
   * lifecycle.
   */
  observe: (sessionId: number) => () => void;

  /**
   * Start an agent turn: plain POST command (202), then observe the run's
   * event stream from seq 0. All progress/terminal state arrives through the
   * same observation path as a reconnect — there is no privileged "starter"
   * stream. On terminal done/cancelled the chat history is refreshed.
   *
   * sessionId is optional and overrides activeSessionId — used by
   * ReportEditPanel to send to a specific report's chat without changing
   * global navigation state.
   */
  sendMessage: (text: string, sessionId?: number) => Promise<void>;

  /**
   * Ask the server to abort the session's run (the ONLY way to stop one —
   * closing observers never cancels). Optimistically flips the view to
   * cancelled so the composer unlocks; the run's terminal event is the
   * authoritative settle.
   */
  cancelRun: (sessionId: number) => Promise<void>;

  reset: () => void;
}

type ChatStore = ChatStateData & ChatActions;

const INITIAL: ChatStateData = {
  sessions: [],
  loadingSessions: false,
  activeSessionId: null,
  messages: [],
  loadingDetail: false,
  sessionRuns: {},
};

/**
 * One client-side subscription per session. Lives outside zustand state:
 * handles are not renderable data. `gen` invalidates callbacks of a replaced
 * or aborted stream; `refs` counts observing components; `retried` limits
 * the silent re-attach after a close-without-terminal to one attempt.
 */
interface Observation {
  refs: number;
  gen: number;
  handle: StreamHandle | null;
  retried: boolean;
}

const observations = new Map<number, Observation>();

export const useChat = create<ChatStore>((set, get) => {
  function setRun(sessionId: number, view: RunView) {
    set((s) => ({ sessionRuns: { ...s.sessionRuns, [sessionId]: view } }));
  }

  // reloadIfActive refreshes the chat history for the observed session if
  // it's still the active one. Skipping when the user has navigated away
  // avoids clobbering messages of an unrelated active chat; the reconnect
  // observation re-delivers the terminal event later and reloads then.
  function reloadIfActive(sessionId: number) {
    if (get().activeSessionId !== sessionId) return;
    void get()
      .selectSession(sessionId)
      .catch((err) => console.warn("post-run refresh failed", err));
  }

  function applyEvent(sessionId: number, ev: AgentEvent, seq?: number) {
    const cur = get().sessionRuns[sessionId];
    const live =
      cur !== undefined &&
      (cur.status === "streaming" || cur.status === "awaiting_clarify");

    let view: RunView;
    if (live) {
      view = { ...cur };
    } else if (cur?.cancelRequested) {
      // The user already stopped watching this turn; drop progress events
      // but let the server's verdict through.
      if (ev.type === "step_start" || ev.type === "step_done") return;
      view = { ...cur };
    } else {
      // Events over a terminal (or absent) view mean a fresh observation:
      // a page-load replay, a recovered stream after an error, or a run
      // started from another surface. Fold into a clean view.
      view = freshRun("streaming");
    }
    if (seq !== undefined) view.lastSeq = seq;

    switch (ev.type) {
      case "step_start": {
        const idx = ev.step_idx ?? view.steps.length;
        const steps = [...view.steps];
        steps[idx] = {
          index: idx,
          tool: ev.tool_name ?? "tool",
          toolInput: ev.tool_input,
          status: "running",
        };
        view.steps = steps;
        view.status = "streaming";
        break;
      }
      case "step_done": {
        const idx = ev.step_idx ?? Math.max(0, view.steps.length - 1);
        const steps = [...view.steps];
        steps[idx] = {
          index: idx,
          tool: ev.tool_name ?? steps[idx]?.tool ?? "tool",
          toolInput: ev.tool_input ?? steps[idx]?.toolInput,
          status: ev.is_error ? "error" : "done",
          durationMs: ev.duration_ms,
        };
        view.steps = steps;
        view.status = "streaming";
        break;
      }
      case "clarify":
        view.status = "awaiting_clarify";
        view.clarifyQuestion = ev.question ?? "";
        break;
      case "done":
        view.status = "idle";
        break;
      case "error":
        view.status = "error";
        view.errorCode = ev.error || "unknown";
        break;
      case "cancelled":
        view.status = "cancelled";
        break;
      default:
        return;
    }

    setRun(sessionId, view);
    if (ev.type === "done" || ev.type === "cancelled") {
      reloadIfActive(sessionId);
    }
  }

  // Opens (or replaces) the session's event stream. Cursor: a live view
  // resumes after its lastSeq; anything else starts from 0 — the server
  // decides whether there is a log worth replaying (204 otherwise).
  function openStream(sessionId: number, fromSeqOverride?: number) {
    let o = observations.get(sessionId);
    if (!o) {
      o = { refs: 0, gen: 0, handle: null, retried: false };
      observations.set(sessionId, o);
    }
    if (o.handle) {
      try {
        o.handle.abort();
      } catch {
        /* already aborted */
      }
    }
    const gen = ++o.gen;
    const obs = o;

    const view = get().sessionRuns[sessionId];
    const liveCursor =
      view &&
      (view.status === "streaming" || view.status === "awaiting_clarify")
        ? view.lastSeq
        : 0;
    const fromSeq = fromSeqOverride ?? liveCursor;

    function streamEnded() {
      if (observations.get(sessionId) !== obs || obs.gen !== gen) return;
      obs.handle = null;
      if (obs.refs <= 0) observations.delete(sessionId);
    }

    obs.handle = observeSessionStream(sessionId, fromSeq, {
      onEvent(ev, seq) {
        if (observations.get(sessionId) !== obs || obs.gen !== gen) return;
        obs.retried = false;
        applyEvent(sessionId, ev, seq);
      },
      onClose() {
        if (observations.get(sessionId) !== obs || obs.gen !== gen) return;
        const cur = get().sessionRuns[sessionId];
        if (cur?.status === "streaming") {
          // Closed without a terminal event: either this subscriber was
          // dropped server-side or the log got cut. One silent re-attach
          // from the cursor; a second failure is surfaced.
          if (!obs.retried) {
            obs.retried = true;
            openStream(sessionId, cur.lastSeq);
            return;
          }
          setRun(sessionId, {
            ...cur,
            status: "error",
            errorCode: "stream_closed",
          });
        }
        streamEnded();
      },
      onError(err) {
        if (observations.get(sessionId) !== obs || obs.gen !== gen) return;
        const cur = get().sessionRuns[sessionId];
        if (
          cur &&
          (cur.status === "streaming" || cur.status === "awaiting_clarify")
        ) {
          setRun(sessionId, {
            ...cur,
            status: "error",
            errorCode: extractErrorCode(err),
          });
        }
        streamEnded();
      },
      onNoRun() {
        if (observations.get(sessionId) !== obs || obs.gen !== gen) return;
        const cur = get().sessionRuns[sessionId];
        if (cur?.status === "streaming") {
          // We believed a run was in flight but the server has nothing:
          // it finished (result already persisted) or was lost to a
          // restart. Settle on the persisted history.
          setRun(sessionId, { ...cur, status: "idle" });
          reloadIfActive(sessionId);
        }
        streamEnded();
      },
    });
  }

  function dropObservation(sessionId: number) {
    const o = observations.get(sessionId);
    if (!o) return;
    o.gen++;
    try {
      o.handle?.abort();
    } catch {
      /* ignore */
    }
    observations.delete(sessionId);
  }

  return {
    ...INITIAL,

    async loadSessions() {
      set({ loadingSessions: true });
      try {
        const sessions = await api.get<ChatSession[]>("/api/chat/sessions");
        set({ sessions, loadingSessions: false });
      } catch (e) {
        set({ loadingSessions: false });
        throw e;
      }
    },

    async selectSession(sessionId) {
      // Distinguish two callers: switching to a different session (where
      // stale messages from the previous chat must be cleared optimistically)
      // vs. reselecting the current session as a refresh (where clearing
      // causes the entire chat to blank for the duration of the round-trip).
      const switching = get().activeSessionId !== sessionId;
      set((s) => ({
        loadingDetail: true,
        activeSessionId: sessionId,
        messages: switching ? [] : s.messages,
      }));
      try {
        const detail = await api.get<SessionDetailResponse>(
          `/api/chat/sessions/${sessionId}`,
        );
        set((s) => ({
          messages: detail.messages,
          sessions: s.sessions.map((x) =>
            x.id === detail.session.id ? detail.session : x,
          ),
          loadingDetail: false,
        }));
      } catch (e) {
        if (switching) {
          // Failed switch leaves a dangling activeSessionId pointing at a
          // chat we couldn't load (e.g. deleted underneath us); clearing it
          // lets ChatPanel's `if (!activeSessionId)` guard render the empty
          // state.
          set({
            loadingDetail: false,
            activeSessionId: null,
            messages: [],
          });
        } else {
          // Reselect refresh: keep the working messages on screen — a failed
          // refresh shouldn't blank a chat the user can already read.
          set({ loadingDetail: false });
        }
        throw e;
      }
    },

    async createSession() {
      const session = await api.post<ChatSession>("/api/chat/sessions");
      set((s) => ({
        sessions: [session, ...s.sessions],
        activeSessionId: session.id,
        messages: [],
      }));
      return session;
    },

    async renameSession(sessionId, name) {
      const session = await api.put<ChatSession>(
        `/api/chat/sessions/${sessionId}/rename`,
        { name },
      );
      set((s) => ({
        sessions: s.sessions.map((x) => (x.id === sessionId ? session : x)),
      }));
    },

    async deleteSession(sessionId) {
      // Optimistic clear: drop the chat from the list and (if it was active)
      // wipe the messages BEFORE the network round-trip so the UI reflects
      // the deletion instantly.
      dropObservation(sessionId);
      const prev = get();
      const wasActive = prev.activeSessionId === sessionId;
      const runs = { ...prev.sessionRuns };
      delete runs[sessionId];
      set({
        sessions: prev.sessions.filter((x) => x.id !== sessionId),
        activeSessionId: wasActive ? null : prev.activeSessionId,
        messages: wasActive ? [] : prev.messages,
        sessionRuns: runs,
      });
      try {
        await api.delete(`/api/chat/sessions/${sessionId}`);
      } catch (e) {
        // Server rejected the delete (network error, FK violation, etc).
        // Roll back local state from the server's truth — the deleted chat
        // will re-appear in the sidebar so the user knows the operation
        // failed.
        try {
          await get().loadSessions();
        } catch {
          /* ignore secondary failure */
        }
        throw e;
      }
    },

    observe(sessionId) {
      let o = observations.get(sessionId);
      if (!o) {
        o = { refs: 0, gen: 0, handle: null, retried: false };
        observations.set(sessionId, o);
      }
      const obs = o;
      obs.refs++;
      if (!obs.handle) openStream(sessionId);

      let detached = false;
      return () => {
        if (detached) return;
        detached = true;
        if (observations.get(sessionId) !== obs) return; // superseded
        obs.refs--;
        if (obs.refs <= 0) {
          obs.gen++;
          try {
            obs.handle?.abort();
          } catch {
            /* ignore */
          }
          observations.delete(sessionId);
        }
      };
    },

    async sendMessage(text, sessionId) {
      const sid = sessionId ?? get().activeSessionId;
      if (sid === null) return;

      // Fresh view for the new turn, unlocked composer only on terminal.
      setRun(sid, freshRun("streaming"));

      // The new run's log restarts at seq 1, so the current stream (and its
      // cursor) is obsolete; it is replaced after the POST lands.
      const o = observations.get(sid);
      if (o?.handle) {
        try {
          o.handle.abort();
        } catch {
          /* ignore */
        }
        o.handle = null;
        o.gen++;
        o.retried = false;
      }

      try {
        // Command: starts the detached run server-side and returns 202. The
        // run is registered before the response, so the observation below
        // cannot miss it.
        await api.post(`/api/chat/sessions/${sid}/messages`, {
          message: text,
        });
      } catch (err) {
        setRun(sid, {
          ...freshRun("error"),
          errorCode: err instanceof ApiError ? err.code : "stream_error",
        });
        return;
      }

      openStream(sid, 0);
    },

    async cancelRun(sessionId) {
      const view = get().sessionRuns[sessionId];
      if (
        !view ||
        (view.status !== "streaming" && view.status !== "awaiting_clarify")
      ) {
        return;
      }

      // Optimistic: unlock the UI now. The observation stays attached — the
      // server's terminal event (cancelled, or done if the run finished
      // first) settles the view and refreshes the history.
      setRun(sessionId, {
        ...view,
        status: "cancelled",
        cancelRequested: true,
      });
      try {
        await api.post(`/api/chat/sessions/${sessionId}/cancel`);
      } catch {
        // Server may already be done — `no_active_stream` is informational.
      }
    },

    reset() {
      for (const [, o] of observations) {
        o.gen++;
        try {
          o.handle?.abort();
        } catch {
          /* ignore */
        }
      }
      observations.clear();
      set({ ...INITIAL, sessionRuns: {} });
    },
  };
});

/**
 * Subscribe a component to a session's RunView, keeping the underlying event
 * stream observation alive while mounted. This is the reconciliation point:
 * "this session is on screen" ⇒ "its run is observed" — no imperative
 * attach/reset choreography in components, so navigation can never orphan or
 * kill a run's view again.
 */
export function useSessionRun(sessionId: number | null): RunView {
  useEffect(() => {
    if (sessionId === null) return;
    return useChat.getState().observe(sessionId);
  }, [sessionId]);
  return useChat((s) =>
    sessionId === null ? IDLE_RUN : (s.sessionRuns[sessionId] ?? IDLE_RUN),
  );
}

function extractErrorCode(err: unknown): string {
  if (err && typeof err === "object") {
    const obj = err as { body?: { error_code?: string; error?: string } };
    if (obj.body?.error_code) return obj.body.error_code;
    if (obj.body?.error) return obj.body.error;
  }
  return "stream_error";
}
